import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Check, MoreHorizontal, Plus } from 'lucide-react';
import { POLICY_KEYS, WS_ROLE_LABELS, WS_ROLES, type WsRole } from '@zentera/shared';
import {
  Badge,
  Button,
  Callout,
  Dialog,
  DialogContent,
  ErrorState,
  Field,
  IconButton,
  Input,
  KeyValueList,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  NativeSelect,
  PageHeader,
  Section,
  Table,
  TableContainer,
  TableMessage,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  cn,
  useToast,
} from '@zentera/ui';
import { api, ApiError, fmt } from '@zentera/web-core';
import { usePageTitle } from '../components/shell';
import { useCan, useMe } from '../lib/auth';

/* ───────────── Início ───────────── */

export function HomePage() {
  usePageTitle('Início');
  const me = useMe();
  const inst = useQuery({ queryKey: ['institution'], queryFn: () => api.get<{ tradeName: string; supportPhone: string | null } | null>('/api/institution') });
  const plans = useQuery({ queryKey: ['plans'], queryFn: () => api.get<{ items: { active: boolean }[] }>('/api/plans') });
  const faq = useQuery({ queryKey: ['faq'], queryFn: () => api.get<{ items: { published: boolean }[] }>('/api/faq') });
  const policies = useQuery({ queryKey: ['policies'], queryFn: () => api.get<{ items: { content: string }[] }>('/api/policies') });

  const filledPolicies = policies.data?.items.filter((p) => p.content.trim()).length ?? 0;
  const steps = [
    { done: !!inst.data?.tradeName, label: 'Dados institucionais', detail: inst.data?.tradeName ?? 'Nome, contatos e área de cobertura', to: '/institution' },
    { done: (plans.data?.items.filter((p) => p.active).length ?? 0) > 0, label: 'Planos', detail: `${plans.data?.items.filter((p) => p.active).length ?? 0} ativos`, to: '/plans' },
    { done: true, label: 'Horário de atendimento', detail: 'Padrão definido; revise se necessário', to: '/hours' },
    { done: filledPolicies === POLICY_KEYS.length, label: 'Políticas', detail: `${filledPolicies} de ${POLICY_KEYS.length} preenchidas`, to: '/policies' },
    { done: (faq.data?.items.filter((f) => f.published).length ?? 0) >= 5, label: 'Perguntas frequentes', detail: `${faq.data?.items.filter((f) => f.published).length ?? 0} publicadas · recomendado ao menos 5`, to: '/faq' },
  ];
  const done = steps.filter((s) => s.done).length;

  return (
    <div className="space-y-10">
      <PageHeader title={inst.data?.tradeName ?? 'Zentera Workspace'} description="Ambiente da sua equipe para manter o conhecimento usado no atendimento e acompanhar os clientes." />
      <Section title="Preparação do assistente" description={`${done} de ${steps.length} itens concluídos. Quanto mais completo, menos atendimentos precisam de transferência.`}>
        <ul className="divide-y divide-line rounded-lg border border-line">
          {steps.map((s) => (
            <li key={s.to}>
              <Link to={s.to} className="group flex items-center gap-3 px-4 py-3">
                <span className={cn('flex size-5 shrink-0 items-center justify-center rounded-full border', s.done ? 'border-fg-2 bg-fg-2 text-white' : 'border-line-strong')}>
                  {s.done && <Check className="size-3" strokeWidth={3} />}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm group-hover:text-brand">{s.label}</div>
                  <div className="truncate text-xs text-fg-3">{s.detail}</div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </Section>
      <Callout tone="neutral" title="Próximo passo: publicar o conhecimento" action={<Button size="sm" asChild><Link to="/assistant">Abrir</Link></Button>}>
        Depois de revisar os itens acima, publique o conhecimento para o assistente passar a usá-lo.
      </Callout>
      <Callout tone="neutral" title="Conecte o assistente ao seu atendimento">
        Você pode integrar o agente ao sistema de atendimento que já utiliza pela API. O adaptador oficial de WhatsApp pode ser adicionado depois sem trocar o motor.
      </Callout>
      {me.data && <p className="text-xs text-fg-4">Versão da instalação {me.data.instanceVersion}</p>}
    </div>
  );
}

/* ───────────── Equipe ───────────── */

interface User { id: string; name: string; email: string; role: WsRole; disabledAt: string | null; lastLoginAt: string | null; mustChangePassword: boolean }

export function TeamPage() {
  usePageTitle('Equipe');
  const can = useCan();
  const me = useMe();
  const qc = useQueryClient();
  const toast = useToast();
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ['users'], queryFn: () => api.get<{ items: User[] }>('/api/users') });
  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { role?: WsRole; disabled?: boolean } }) => api.patch(`/api/users/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
    onError: (e) => toast({ tone: 'danger', title: 'Não foi possível alterar', description: e instanceof ApiError ? e.message : undefined }),
  });
  return (
    <div className="space-y-5">
      <PageHeader
        title="Equipe"
        description="Pessoas do provedor com acesso a este Workspace."
        actions={can('team.write') && <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>Adicionar pessoa</Button>}
      />
      <TableContainer>
        <Table>
          <THead><tr><Th>Nome</Th><Th>Papel</Th><Th>Situação</Th><Th>Último acesso</Th><Th className="w-10"><span className="sr-only">Ações</span></Th></tr></THead>
          <TBody>
            {q.isPending ? <TableSkeleton columns={5} rows={3} /> : q.isError ? (
              <TableMessage columns={5}><ErrorState error={q.error} /></TableMessage>
            ) : (
              q.data.items.map((u) => (
                <Tr key={u.id} className={cn(u.disabledAt && 'opacity-60')}>
                  <Td><div className="font-medium">{u.name}</div><div className="text-xs text-fg-3">{u.email}</div></Td>
                  <Td>{WS_ROLE_LABELS[u.role]}</Td>
                  <Td>{u.disabledAt ? <Badge tone="muted">Desativado</Badge> : u.mustChangePassword ? <Badge tone="info">Senha temporária</Badge> : 'Ativo'}</Td>
                  <Td className="text-fg-2">{u.lastLoginAt ? fmt.relative(u.lastLoginAt) : 'Nunca acessou'}</Td>
                  <Td>
                    {can('team.write') && u.id !== me.data?.user.id && (
                      <Menu>
                        <MenuTrigger asChild><IconButton size="sm" label={`Ações de ${u.name}`} icon={<MoreHorizontal />} /></MenuTrigger>
                        <MenuContent>
                          {WS_ROLES.filter((r) => r !== u.role).map((r) => (
                            <MenuItem key={r} onSelect={() => update.mutate({ id: u.id, body: { role: r } })}>Tornar {WS_ROLE_LABELS[r].toLowerCase()}</MenuItem>
                          ))}
                          <MenuItem danger={!u.disabledAt} onSelect={() => update.mutate({ id: u.id, body: { disabled: !u.disabledAt } })}>
                            {u.disabledAt ? 'Reativar acesso' : 'Desativar acesso'}
                          </MenuItem>
                        </MenuContent>
                      </Menu>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>
      {creating && <CreateUserDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateUserDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WsRole>('attendant');
  const [password] = useState(() => `${btoa(String.fromCharCode(...crypto.getRandomValues(new Uint8Array(12)))).replace(/[+/=]/g, '')}5w`);
  const [created, setCreated] = useState(false);
  const create = useMutation({
    mutationFn: () => api.post('/api/users', { name, email, role, temporaryPassword: password }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['users'] }); setCreated(true); },
  });
  const e = create.error instanceof ApiError ? create.error.fieldErrors() : {};
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={created ? 'Acesso criado' : 'Adicionar pessoa'}
        description={created ? 'Envie a senha temporária por um canal seguro. Ela não será exibida novamente.' : 'A pessoa define a própria senha no primeiro acesso.'}
        footer={created ? <Button variant="primary" onClick={onClose}>Concluir</Button> : <><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button variant="primary" loading={create.isPending} disabled={!name || !email} onClick={() => create.mutate()}>Criar acesso</Button></>}
      >
        {created ? (
          <KeyValueList items={[{ label: 'E-mail', value: email }, { label: 'Senha temporária', value: <code className="font-mono text-xs">{password}</code> }]} />
        ) : (
          <div className="space-y-4">
            {create.error && Object.keys(e).length === 0 && <p role="alert" className="text-sm text-danger">{(create.error as Error).message}</p>}
            <Field label="Nome" error={e.name}><Input value={name} onChange={(ev) => setName(ev.target.value)} autoFocus /></Field>
            <Field label="E-mail" error={e.email}><Input type="email" value={email} onChange={(ev) => setEmail(ev.target.value)} /></Field>
            <Field label="Papel">
              <NativeSelect value={role} onChange={(ev) => setRole(ev.target.value as WsRole)}>
                {WS_ROLES.map((r) => <option key={r} value={r}>{WS_ROLE_LABELS[r]}</option>)}
              </NativeSelect>
            </Field>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ───────────── Atividade ───────────── */

const ACTIONS: Record<string, string> = {
  'auth.login': 'Entrou',
  'auth.login_failed': 'Tentativa de login falhou',
  'auth.password_changed': 'Alterou a senha',
  'institution.updated': 'Atualizou dados institucionais',
  'plan.created': 'Cadastrou plano',
  'plan.updated': 'Alterou plano',
  'plan.deleted': 'Removeu plano',
  'business_hours.updated': 'Alterou horários',
  'policy.updated': 'Alterou política',
  'faq.created': 'Cadastrou pergunta',
  'faq.updated': 'Alterou pergunta',
  'faq.deleted': 'Removeu pergunta',
  'document.created': 'Adicionou documento',
  'document.deleted': 'Removeu documento',
  'knowledge.published': 'Publicou o conhecimento',
  'engine.config_applied': 'Configuração do assistente atualizada pela Zentera',
  'ai.credential_updated': 'Credencial de IA atualizada pela Zentera',
  'user.created': 'Criou acesso',
  'user.updated': 'Alterou acesso',
};

export function ActivityPage() {
  usePageTitle('Atividade');
  const q = useQuery({ queryKey: ['audit'], queryFn: () => api.get<{ items: { id: string; at: string; actorLabel: string; action: string; targetId: string | null }[] }>('/api/audit?limit=100') });
  return (
    <div className="space-y-5">
      <PageHeader title="Atividade" description="Registro das alterações feitas neste Workspace. Não pode ser editado." />
      <TableContainer>
        <Table>
          <THead><tr><Th>Quando</Th><Th>Ação</Th><Th>Pessoa</Th></tr></THead>
          <TBody>
            {q.isPending ? <TableSkeleton columns={3} rows={6} /> : q.isError ? (
              <TableMessage columns={3}><ErrorState error={q.error} /></TableMessage>
            ) : (
              q.data.items.map((a) => (
                <Tr key={a.id}>
                  <Td className="tabular text-fg-2">{fmt.dateTime(a.at)}</Td>
                  <Td className={cn(a.action.endsWith('_failed') && 'text-danger')}>{ACTIONS[a.action] ?? a.action}</Td>
                  <Td className="text-fg-2">{a.actorLabel}</Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>
    </div>
  );
}
